import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useGuidanceRequestTemplate } from "@/hooks/use-app-settings";
import { useRequestGuidanceForAudit } from "@/hooks/use-audits";
import { renderGuidanceTemplate } from "@/lib/guidance-template";

interface RequestGuidanceForAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditId: string;
  projectName: string;
  demandName?: string | null;
  auditorName: string;
  templateName: string;
}

const schema = z.object({
  guidance: z
    .string()
    .trim()
    .min(1, "Guidance hours is required")
    .refine((v) => Number.isFinite(Number(v)) && Number(v) > 0, "Enter a positive number of hours"),
  message: z.string().trim().min(1, "Guidance request text is required"),
});

type FormValues = z.infer<typeof schema>;

function LockedField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium leading-none">{label}</p>
      <p className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
        {value}
      </p>
    </div>
  );
}

export function RequestGuidanceForAuditDialog({
  open,
  onOpenChange,
  auditId,
  projectName,
  demandName,
  auditorName,
  templateName,
}: RequestGuidanceForAuditDialogProps) {
  const { data: messageTemplate } = useGuidanceRequestTemplate();
  const requestGuidance = useRequestGuidanceForAudit(auditId);
  const [messageDirty, setMessageDirty] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { guidance: "", message: "" },
  });

  const guidance = form.watch("guidance");

  // Reset the form and re-arm auto-fill whenever the pop-up is (re)opened.
  useEffect(() => {
    if (open) {
      setMessageDirty(false);
      form.reset({ guidance: "", message: "" });
    }
  }, [open, form]);

  // Keep the free-text body in sync with the template + current selections
  // until the user edits it directly — at that point their edits win.
  useEffect(() => {
    if (!open || messageDirty) return;
    const rendered = renderGuidanceTemplate(messageTemplate?.template ?? "", {
      project_name: projectName,
      demand_name: demandName ?? "",
      auditor_name: auditorName,
      guidance,
    });
    form.setValue("message", rendered);
  }, [open, messageDirty, messageTemplate, projectName, demandName, auditorName, guidance, form]);

  async function onSubmit(values: FormValues) {
    await requestGuidance.mutateAsync(values.message);
    form.reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request Guidance</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <LockedField label="Project" value={projectName} />
            <LockedField label="Template" value={templateName} />
            <LockedField label="Auditor" value={auditorName} />
            <FormField
              control={form.control}
              name="guidance"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Guidance (hours)</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="0.5" placeholder="e.g. 2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Guidance Request</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe what you'd like guidance on…"
                      rows={6}
                      {...field}
                      onChange={(e) => {
                        setMessageDirty(true);
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={requestGuidance.isPending}>
                {requestGuidance.isPending ? "Sending…" : "Send"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
